"use client";

import { useState, useCallback, type ReactNode } from "react";
import { apiClient, type ResourceResponse } from "@/lib/apiClient";
import { FileViewerDialog, type FileOutput } from "./file-viewer";
import { Loader2Icon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ResourceLink component
 *
 * Renders a clickable link that fetches resource content from the API
 * and displays it in a file viewer modal.
 *
 * Used in markdown rendering to intercept /resources/{id} links.
 */
interface ResourceLinkProps {
  resourceId: string;
  children: ReactNode;
  className?: string;
}

export function ResourceLink({ resourceId, children, className }: ResourceLinkProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<FileOutput | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If already loaded, just open the dialog
    if (file) {
      setIsOpen(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const resource = await apiClient.getResource(resourceId);

      // Convert ResourceResponse to FileOutput for the viewer
      const fileOutput: FileOutput = {
        type: "file",
        file_id: resource.resource_id,
        filename: resource.filename,
        content: resource.content,
        mime_type: resource.content_type,
        size: resource.size,
        url: apiClient.getResourceRawUrl(resourceId),
      };

      setFile(fileOutput);
      setIsOpen(true);
    } catch (err) {
      console.error("Failed to fetch resource:", err);
      setError(err instanceof Error ? err.message : "Failed to load resource");
    } finally {
      setIsLoading(false);
    }
  }, [resourceId, file]);

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={cn(
          "inline-flex items-center gap-1 font-medium text-primary underline underline-offset-4",
          "hover:text-primary/80 transition-colors cursor-pointer",
          "disabled:opacity-50 disabled:cursor-wait",
          className
        )}
        title={error || `View ${children}`}
      >
        {isLoading && <Loader2Icon className="h-3 w-3 animate-spin" />}
        {children}
        {error && <span className="text-red-400 text-xs ml-1">(error)</span>}
      </button>

      {file && (
        <FileViewerDialog
          file={file}
          open={isOpen}
          onOpenChange={setIsOpen}
        />
      )}
    </>
  );
}

/**
 * Helper to check if a URL is a resource link
 */
export function isResourceLink(href: string | undefined): boolean {
  if (!href) return false;
  return href.startsWith("/resources/") || href.startsWith("resources/");
}

/**
 * Helper to extract resource ID from a resource URL
 */
export function extractResourceId(href: string): string {
  // Handle both /resources/{id} and resources/{id}
  const match = href.match(/\/?resources\/([^/]+)/);
  return match ? match[1] : "";
}
